import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiExternalLink, FiX } from 'react-icons/fi'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-use'
import { getAccessToken } from '../../reducers/userAccount/selectors/credentials'
import styled from '../../theme'

/** ms — baner wraca po każdym zamknięciu (X) po tym czasie od momentu zamknięcia */
const BANNER_HIDE_DURATION_MS = 48 * 60 * 60 * 1000

const BETA_BANNER_DISMISSED_AT_KEY = 'bb_beta_calendar_banner_dismissed_at'

function shouldShowBetaBanner(): boolean {
  if (typeof window === 'undefined') {
    return true
  }
  const raw = window.localStorage.getItem(BETA_BANNER_DISMISSED_AT_KEY)
  if (!raw) {
    return true
  }
  const dismissedAt = Number(raw)
  if (!Number.isFinite(dismissedAt)) {
    return true
  }
  return Date.now() - dismissedAt >= BANNER_HIDE_DURATION_MS
}

const COLORS = {
  bg: '#FDF8F0',
  border: '#EFE5D3',
  close: '#272728',
  ctaActiveBg: '#EBDBB8',
  ctaBorder: '#C4A96F',
  ctaHoverBg: '#F3E5C9',
  ctaHoverText: '#573C0F',
  text: '#6B4B14',
  textSecondary: '#000000',
} as const

export const BETA_BANNER_HEIGHT_CSS_VAR = '--beta-banner-height'
/** szacunkowa wysokość desktop — ustawiana od razu, dopóki ResizeObserver nie zmierzy dokładnie */
export const BETA_BANNER_ESTIMATED_HEIGHT_PX = 56

const Root = styled.div<{ size?: number; embedded?: boolean }>`
  align-items: center;
  background: ${COLORS.bg};
  border-bottom: 1px solid ${COLORS.border};
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  gap: 16px;
  justify-content: space-between;
  min-height: 56px;
  padding: ${({ embedded, size }) =>
    embedded ? '6px 0' : `6px 24px 6px ${size ? 80 * size : 80}px`};
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 0;
    padding: ${({ embedded }) => (embedded ? '10px 0' : '10px')};
  }
`

const Content = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  gap: 8px;
  line-height: 18px;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 6px;
  }
`

const IconWrap = styled.span`
  align-items: center;
  color: ${COLORS.text};
  display: flex;
  flex-shrink: 0;
  height: 22px;
  justify-content: center;
  width: 22px;
`

function MessageCircleCheckIcon() {
  return (
    <svg
      aria-hidden
      fill="none"
      height="22"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.9"
      viewBox="0 0 24 24"
      width="22"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}

const Body = styled.div`
  min-width: 0;
`

const Title = styled.span`
  color: ${COLORS.text};
  font-weight: 600;
`

const Description = styled.span`
  color: ${COLORS.textSecondary};
  font-weight: 400;
`

const CtaButton = styled.button`
  align-items: center;
  background: transparent;
  border: 1px solid ${COLORS.ctaBorder};
  border-radius: 6px;
  color: ${COLORS.text};
  cursor: pointer;
  display: inline-flex;
  font-family: inherit;
  font-size: inherit;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0.002em;
  line-height: inherit;
  margin: 0;
  padding: 4px 10px;
  transition: background-color 0.15s ease, color 0.15s ease;
  vertical-align: baseline;
  white-space: nowrap;

  &:hover {
    background: ${COLORS.ctaHoverBg};
    color: ${COLORS.ctaHoverText};
  }

  &:active {
    background: ${COLORS.ctaActiveBg};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.text};
    outline-offset: 2px;
  }
`

const ExternalIcon = styled(FiExternalLink)`
  flex-shrink: 0;
  font-size: 12px;
`

const CloseButton = styled.button`
  align-items: center;
  align-self: center;
  background: none;
  border: none;
  color: ${COLORS.close};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  margin: 0;
  padding: 4px;

  &:focus-visible {
    outline: 2px solid ${COLORS.close};
    outline-offset: 2px;
  }
`

const NATIVE_CALENDAR_PATH = '/booking/calendar'
const NATIVE_APP_URL = process.env.REACT_APP_NATIVE_APP_URL

function isCalendarPath(pathname: string): boolean {
  return pathname === '/' || pathname.includes('/booking/calendar')
}

function setBannerHeightCssVar(heightPx: number) {
  document.documentElement.style.setProperty(
    BETA_BANNER_HEIGHT_CSS_VAR,
    `${heightPx}px`,
  )
}

export default function BetaInfoBanner({
  size,
  embedded = false,
}: {
  size?: number
  embedded?: boolean
}) {
  const { t } = useTranslation()
  const location = useLocation()
  const pathname = location && location.pathname ? location.pathname : ''
  const [visible, setVisible] = useState(() => shouldShowBetaBanner())
  const accessToken = useSelector(getAccessToken)
  const rootRef = useRef<HTMLDivElement>(null)
  const isCalendarRoute = isCalendarPath(pathname)
  const shouldRender = visible && isCalendarRoute && !!NATIVE_APP_URL

  useLayoutEffect(() => {
    if (!shouldRender) {
      setBannerHeightCssVar(0)
      return
    }

    setBannerHeightCssVar(BETA_BANNER_ESTIMATED_HEIGHT_PX)

    const el = rootRef.current
    if (!el) {
      return
    }

    const syncHeight = () => setBannerHeightCssVar(el.offsetHeight)

    syncHeight()
    const observer = new ResizeObserver(syncHeight)
    observer.observe(el)

    return () => {
      observer.disconnect()
      setBannerHeightCssVar(0)
    }
  }, [shouldRender])

  const onClose = useCallback(() => {
    setBannerHeightCssVar(0)
    const now = Date.now()
    try {
      window.localStorage.setItem(BETA_BANNER_DISMISSED_AT_KEY, String(now))
    } catch {
      // ignore quota / private mode
    }
    setVisible(false)
  }, [])

  const onCta = useCallback(() => {
    if (!NATIVE_APP_URL) {
      return
    }
    const params = new URLSearchParams()
    if (accessToken) {
      params.set('accessToken', accessToken)
    }
    const url = `${NATIVE_APP_URL}${NATIVE_CALENDAR_PATH}?${params.toString()}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [accessToken])

  if (!shouldRender) {
    return null
  }

  return (
    <Root
      ref={rootRef}
      size={size}
      embedded={embedded}
      data-cy="beta-info-banner"
    >
      <Content>
        <IconWrap aria-hidden>
          <MessageCircleCheckIcon />
        </IconWrap>
        <Body>
          <Title>{t('BED_16772_new_design_title')}</Title>{' '}
          <Description>{t('BED_16772_preview_now_label')}</Description>{' '}
          <CtaButton
            type="button"
            onClick={onCta}
            data-cy="beta-info-banner-cta"
          >
            {t('BED_16772_change_cta')}
            <ExternalIcon aria-hidden />
          </CtaButton>
        </Body>
      </Content>
      <CloseButton
        type="button"
        aria-label={t('application_block_dialog_close_btn')}
        onClick={onClose}
        data-cy="beta-info-banner-close"
      >
        <FiX size={20} />
      </CloseButton>
    </Root>
  )
}
